import React from 'react';
import PropTypes from 'prop-types';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Timeline from '@mui/lab/Timeline';
import EventRepeat from '@mui/icons-material/EventRepeat';
import AddIcon from '@mui/icons-material/Add';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import {
  useSelector,
  useDispatch,
} from 'react-redux';

import medicalIcon from '../../../../../images/unused-undraw-illustrations/medical.svg';
import NoteItem from '../../../../../components/NoteItem';
import HorseDueDates from '../../../../../components/HorseDueDates';
import {
  getHorseNotes,
  getHorseNoteHighlights,
} from '../../../../../redux/horse/actions';

const HealthSectionComponent = ({
  id,
  isAdmin,
  openAddEditNoteModal,
  condensed,
}) => {
  const dispatch = useDispatch();

  // Redux state
  const horse = useSelector((state) => state.horse.horse);
  const horseNotes = useSelector((state) => state.horse.horseNotes);
  const isFetchingHorseNotes = useSelector((state) => state.horse.isFetchingHorseNotes);
  const currentHorseLodging = useSelector((state) => state.horse.currentHorseLodging);

  // State
  const [pageSize] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [sortModel] = React.useState([
    {
      field: 'createdTimestamp',
      sort: 'desc',
    },
  ]);
  const [noteTypeFilter] = React.useState('Medical');

  React.useEffect(() => {
    dispatch(getHorseNotes(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`), noteTypeFilter || null, null));
    dispatch(getHorseNoteHighlights(id));
  }, [id, page, noteTypeFilter]);

  const closeAddEditModalCallbcak = () => {
    // reload
    dispatch(getHorseNotes(id, (page - 1), pageSize, sortModel.map((s) => `&sort=${s.field},${s.sort}`), noteTypeFilter || null, null));
  };

  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        md={condensed
          ? 12
          : 6}
      >
        <Card>
          <CardHeader
            avatar={<Avatar><MedicalServicesIcon color="primary" /></Avatar>}
            action={(
              <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={() => openAddEditNoteModal(null, noteTypeFilter, closeAddEditModalCallbcak)}>Add Health Note</Button>
            )}
            title="Health Notes"
          />
          <CardContent>
            <Timeline>
              {
                horseNotes.content.map((note) => (
                  <NoteItem
                    note={note}
                    horseId={id}
                    pinnedHorseNoteId={horse.activeNote
                      ? horse.activeNote.id
                      : null}
                    key={note.id}
                    includeUserInfo
                    isLoading={isFetchingHorseNotes}
                    onEdit={() => openAddEditNoteModal(note, noteTypeFilter, closeAddEditModalCallbcak)}
                    isAdmin={isAdmin}
                  />
                ))
              }
              {
                (!isFetchingHorseNotes && horseNotes.content.length === 0) && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle1" align="center" component="div" sx={{ m: 4 }}>
                      No Health Notes
                    </Typography>
                    <img
                      src={medicalIcon}
                      alt="doctor with clipboard"
                      style={{
                        width: 250,
                        display: 'block',
                        margin: 'auto',
                        marginBottom: '1rem',
                      }}
                    />
                  </Box>
                )
              }
            </Timeline>
            <Pagination count={horseNotes.totalPages} variant="outlined" page={page} onChange={(event, value) => { setPage(value); }} disabled={isFetchingHorseNotes} />
          </CardContent>
        </Card>
      </Grid>
      <Grid
        item
        xs={12}
        md={condensed
          ? 12
          : 6}
      >
        <Card>
          <CardHeader
            avatar={<Avatar><EventRepeat color="primary" /></Avatar>}
            title="Health Tasks"
            subheader={(
              <>Reminders setup with tasks having the <b>Health</b> tag will be displayed below. This horse must also be added to that reminder.
                {isAdmin && <>&nbsp;You can create new health tasks or tag existing ones from the <a href="/settings/tasks">task settings page</a>.</>}
                {isAdmin && <>&nbsp;Manage reminders for this horse from the <a href="/settings/tasks/reminders">reminder settings page</a>.</>}
              </>
            )}
          />
        </Card>
        <Box sx={{ mt: 2 }}>
          <HorseDueDates isAdmin barnHorse={currentHorseLodging} health />
        </Box>
      </Grid>
    </Grid>
  );
};

HealthSectionComponent.propTypes = {
  id: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  openAddEditNoteModal: PropTypes.func.isRequired,
  condensed: PropTypes.bool,
};

HealthSectionComponent.defaultProps = { condensed: false };

export default HealthSectionComponent;
